import { useMemo } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import OffersService from '~/services/OffersService';
import PublicOffersService from '~/services/PublicOfferService';
import SearchService, { TypeaheadType } from '~/services/SearchService';

import { HotelOffer } from './useHotelOffersByPlaceIds';

interface UseHotelOffersByFiltersParams {
  idIn?: string | null;
  title?: string | null;
  enabled?: boolean;
}

const STALE_TIME = 5 * 60 * 1000;
const region = 'AU';

const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = ['le_property', 'le_property_unique_stays'];

export function useHotelOffersByFilters({ idIn, title, enabled = true }: UseHotelOffersByFiltersParams) {
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const offerIds = useMemo(() => {
    if (idIn) {
      return idIn
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }
    return [];
  }, [idIn]);

  const offerByIdQueries = useQueries({
    queries: offerIds.map((offerId) => ({
      queryKey: ['hotel-offer-by-id', offerId, brand],
      queryFn: async (): Promise<HotelOffer | null> => {
        try {
          const isBedbank = offerId.length === 36;
          if (isBedbank) {
            const response = await PublicOffersService.getBedbankOfferById(offerId, region, brand);
            return {
              id: offerId,
              title: response.result.name || offerId,
              imageId: response.result.images?.[0]?.id,
            };
          } else {
            const response = await OffersService.getOffer(offerId);
            return {
              id: offerId,
              title: response.result.name,
              imageId: response.result.images?.[0]?.id_cloudinary_external,
            };
          }
        } catch (error) {
          enqueueSnackbar(`Error fetching offer ${offerId}: ${error.message}`, { variant: 'error' });
          return null;
        }
      },
      enabled: enabled && !!offerId,
      staleTime: STALE_TIME,
    })),
  });

  const searchByTitleQuery = useQuery({
    queryKey: ['hotel-offers-by-title', title, brand],
    queryFn: async (): Promise<Array<HotelOffer>> => {
      if (!title || title.length < 2) {
        return [];
      }

      try {
        const typeaheadResult = await SearchService.typeahead(title, {
          type: RELEVANT_TYPEAHEAD_TYPES,
          brand: brand.toLowerCase(),
          region: 'AU',
          limit: 100,
          priority: 'current',
        });

        const offerIdsFromTypeahead = typeaheadResult.result
          .map((result) => result.offerId || result.id)
          .filter(Boolean);

        if (offerIdsFromTypeahead.length === 0) {
          return [];
        }

        const offerDetailsPromises = offerIdsFromTypeahead.map(async (offerId: string) => {
          try {
            const isBedbank = offerId.length === 36;
            if (isBedbank) {
              const response = await PublicOffersService.getBedbankOfferById(offerId, region, brand);
              return {
                id: offerId,
                title: response.result.name || offerId,
                imageId: response.result.images?.[0]?.id,
              };
            } else {
              const response = await OffersService.getOffer(offerId);
              return {
                id: offerId,
                title: response.result.name,
                imageId: response.result.images?.[0]?.id_cloudinary_external,
              };
            }
          } catch (error) {
            return null;
          }
        });

        const offerDetails = await Promise.all(offerDetailsPromises);
        return offerDetails.filter((offer) => offer !== null);
      } catch (error) {
        enqueueSnackbar(`Error searching hotels by title: ${error.message}`, { variant: 'error' });
        return [];
      }
    },
    enabled: enabled && !!title && title.length >= 2,
    staleTime: STALE_TIME,
  });

  const offers = useMemo(() => {
    if (idIn) {
      return offerByIdQueries
        .map((query) => query.data)
        .filter((offer): offer is HotelOffer => offer !== null && offer !== undefined);
    }
    if (title) {
      return searchByTitleQuery.data || [];
    }
    return [];
  }, [idIn, title, offerByIdQueries, searchByTitleQuery.data]);

  const isLoading =
    (idIn && offerByIdQueries.some((query) => query.isLoading)) || (title && searchByTitleQuery.isLoading);
  const isError = (idIn && offerByIdQueries.some((query) => query.isError)) || (title && searchByTitleQuery.isError);

  return {
    offers,
    isLoading,
    isError,
  };
}
